import {clsx} from 'clsx'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {SimpleDiffLine} from '../types'
import {getBackgroundColor, lineIdentifierFrom} from '../diff-line-helpers'

interface DiffTextProps extends React.ComponentProps<'td'> {
  colSpan?: number
  isHighlighted?: boolean
  isLeftColumn?: boolean
  line: SimpleDiffLine
  lineAnchor?: string
  lineChild?: React.ReactNode
  dragging?: boolean
}

/**
 * Renders the diff text
 */
export const DiffText = (props: DiffTextProps) => {
  const {dragging, isHighlighted, isLeftColumn, colSpan, line, lineAnchor} = props
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
  let lineHTML: SafeHTMLString = line.html as SafeHTMLString
  let lineTypeChar = ''

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  if (['ADDITION', 'DELETION'].includes(line.type) && ['+', '-'].includes(lineHTML[0]!)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lineTypeChar = lineHTML[0]!
    /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Composed from sanitized segments */
    lineHTML = lineHTML.slice(1) as SafeHTMLString
  }

  return (
    <td
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className={clsx('diff-text-cell', {
        'border-left color-border-accent-emphasis': dragging && isHighlighted,
        'border-right': isLeftColumn && line.type !== 'HUNK',
      })}
      colSpan={colSpan}
      id={lineAnchor ? lineIdentifierFrom(lineAnchor) : undefined}
      style={{backgroundColor: getBackgroundColor(line.type, false, isHighlighted)}}
    >
      <code
        data-code-marker={lineTypeChar}
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className={clsx('diff-text syntax-highlighted-line', {
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          addition: line.type === 'ADDITION',
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          deletion: line.type === 'DELETION',
        })}
      >
        {props.lineChild}
        {/* Explicitly mark html as safe because it is server-sanitized */}
        <VerifiedHTMLDiv
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx('diff-text-inner', {
            'color-fg-muted': line.type === 'HUNK',
          })}
          html={lineHTML}
          style={{
            // todo: determine why context lines have an extra space at the beginning
            marginLeft: line.type === 'CONTEXT' ? '-7px' : undefined,
          }}
        />
      </code>
    </td>
  )
}
